import { api } from '../serverClient';
import { CURRENCY } from './profileDefaults';
import { ALPHA_PRECONS, ALPHA_PRECON_PRICE, isAlphaPreconKey } from './alphaPrecons';
import { slotRarityForId } from '../sorcery/slotRarityOverrides';
import { resolvePrinting } from '../sorcery/foil';

// Store-visible booster sets. The server rolls the actual contents, but
// the client still needs display metadata (label) and the wire key that
// the server accepts on /packs/purchase. The server enforces that only
// these keys are accepted; keep this in sync with BOOSTER_SETS in
// valkenhall-server/src/utils/packs.js.
export const BOOSTER_SETS = {
  gothic:    { name: 'gothic',    label: 'Gothic' },
  arthurian: { name: 'arthurian', label: 'Arthurian Legends' },
  beta:      { name: 'beta',      label: 'Beta' },
};

// Sealed boxes. Non-randomized sealed product. Each box delivers a
// fixed playset of its set plus one random foil duplicate. Purchased
// through the same /packs/purchase endpoint with the box's setKey;
// the server routes box keys to its box generator. Keep in sync with
// SEALED_BOXES in valkenhall-server/src/utils/packs.js.
export const SEALED_BOXES = {
  dragonlord: {
    name: 'dragonlord',
    label: 'Dragonlord Box',
    price: 690,
    description: 'One copy of every Dragonlord card · 13 unique cards plus a random foil',
    cardCount: 14,
  },
};

// Sites Boosters. Same 15-card layout and foil odds as a normal
// booster, but every slot draws from the Site bucket of the chosen
// set. Lets atlas-focused players target sites without rolling
// minions / magic on every draw. Keep in sync with SITES_BOOSTERS in
// valkenhall-server/src/utils/packs.js.
// `featuredSiteSlug` is the horizontal site-card art shown behind the
// booster pack in the store tile. Picked per set to convey "this
// booster is filled with sites of this flavor" at a glance.
export const SITES_BOOSTERS = {
  sitesBeta:      { name: 'sitesBeta',      label: 'Beta Site Booster',      sourceSetKey: 'beta',      featuredSiteSlug: 'bet-cloud_city-b-s',  price: CURRENCY.PACK_PRICE },
  sitesArthurian: { name: 'sitesArthurian', label: 'Arthurian Site Booster', sourceSetKey: 'arthurian', featuredSiteSlug: 'art-camelot-b-s',     price: CURRENCY.PACK_PRICE },
  sitesGothic:    { name: 'sitesGothic',    label: 'Gothic Site Booster',    sourceSetKey: 'gothic',    featuredSiteSlug: 'got-hellmouth-b-s',   price: CURRENCY.PACK_PRICE },
};

export function isSealedBoxKey(setKey) {
  return (
    Object.prototype.hasOwnProperty.call(SEALED_BOXES, setKey) ||
    isAlphaPreconKey(setKey)
  );
}

export function isSitesBoosterKey(setKey) {
  return Object.prototype.hasOwnProperty.call(SITES_BOOSTERS, setKey);
}

export const PACK_SIZE = 15;

// Resolve a single server-rolled pack entry against the client's
// sorceryCards index. Server sends { cardId, printingId, foiling, rarity };
// the pack-opening UI needs { card, printing } with the full card object
// so it can render art, name, stats, etc.
//
// If the card or its printing isn't found (e.g. the client's card data
// is stale), the entry is skipped. That shouldn't happen in practice
// because the server pool is built from the same sorcery-cards.json, but
// we degrade gracefully instead of crashing the UI.
export function resolvePackContents(contents, sorceryCards) {
  if (!Array.isArray(contents) || !Array.isArray(sorceryCards)) return [];

  const cardsById = new Map();
  for (const card of sorceryCards) {
    if (card?.unique_id) cardsById.set(card.unique_id, card);
  }

  const resolved = [];
  for (const entry of contents) {
    const card = cardsById.get(entry.cardId);
    if (!card) continue;
    const printing = resolvePrinting(card, entry.printingId, entry.foiling || 'S') || {};
    const rarity = entry.rarity || card.rarity || 'Ordinary';
    // `rarity` is the displayed stamp; `slotRarity` is the distribution
    // rarity the re-roll / transmute actions key off. They diverge for AL
    // Unique-Knights (shown Unique, distributed Elite). Keep them split so
    // a budget knight re-rolls/transmutes as Elite, not Unique.
    resolved.push({ card, printing, rarity, slotRarity: slotRarityForId(entry.cardId, rarity) });
  }
  return resolved;
}

// Default pack flags (Transmutation usage state). Preserved on
// resolved packs so the pack-opening UI can grey out re-roll buttons
// for rarities the player has already used on this pack.
export function defaultPackFlags() {
  return { rerolledRarities: [], transmuted: false };
}

function normalizeFlags(raw) {
  if (!raw || typeof raw !== 'object') return defaultPackFlags();
  return {
    rerolledRarities: Array.isArray(raw.rerolledRarities)
      ? raw.rerolledRarities.filter((r) => typeof r === 'string')
      : [],
    transmuted: Boolean(raw.transmuted),
  };
}

// Transform a server pack row { id, setKey, contents, flags } into
// the shape the local state uses: { id, setKey, cards, flags }. The
// id is the pending_packs row id, which the client sends back on open.
export function resolvePack(pack, sorceryCards) {
  return {
    id: pack.id,
    setKey: pack.setKey,
    cards: resolvePackContents(pack.contents, sorceryCards),
    contents: Array.isArray(pack.contents) ? pack.contents : [],
    flags: normalizeFlags(pack.flags),
  };
}

export function resolvePendingPacks(pendingPacks, sorceryCards) {
  if (!Array.isArray(pendingPacks)) return [];
  return pendingPacks.map((p) => resolvePack(p, sorceryCards));
}

// Buy N packs of a set. Server rolls contents, deducts coins atomically,
// and returns the freshly-rolled packs plus the new coin balance.
export async function purchasePacks(setKey, quantity) {
  return api.post('/profile/me/packs/purchase', { setKey, quantity });
}

// Open a pending pack by server id. Server stamps opened_at, grants
// the contents on first call, and returns { pack, collection,
// alreadyOpened }. Idempotent. Calling open on a pack the server
// already opened (e.g. after a reload mid-session) returns the same
// contents without re-granting.
export async function openPendingPack(packId) {
  return api.post(`/profile/me/packs/${encodeURIComponent(packId)}/open`, {});
}

// Delete the pending_packs row after the player has exited the
// opening screen. Cards were already granted by /open; this just
// reclaims the server-side scaffolding for the Transmutation session.
// Idempotent. Safe to call after the row is already gone.
export async function finalizePack(packId) {
  return api.post(`/profile/me/packs/${encodeURIComponent(packId)}/finalize`, {});
}

// Re-roll one card in a pending pack using a Transmutation re-roll
// node (transmuter_2..5). Server validates skill + rarity + per-pack
// usage and returns the updated pack plus the freshly-rolled card.
export async function rerollPackCard(packId, cardIndex) {
  return api.post(
    `/profile/me/packs/${encodeURIComponent(packId)}/reroll`,
    { cardIndex },
  );
}

// Promote one card up a rarity tier using the Transmute capstone
// (transmuter_6). One use per pack. Server enforces the flag.
export async function transmutePackCard(packId, cardIndex) {
  return api.post(
    `/profile/me/packs/${encodeURIComponent(packId)}/transmute`,
    { cardIndex },
  );
}
